// main.ts — browser UI for the WebADB ingest app (EGO-57/64).
// Connection lifecycle + session/collection listing + pull with the
// verify-then-delete semantics from src/pull.ts. The `__e2e` hooks at the
// bottom let an automated driver run the whole path against OPFS (no native
// dialogs), which is how the overnight E2E exercises it.
import { Adb, AdbDaemonTransport } from "@yume-chan/adb";
import { AdbDaemonWebUsbDeviceManager } from "@yume-chan/adb-daemon-webusb";
import AdbWebCredentialStore from "@yume-chan/adb-credential-web";
import { listRemote, pullSession, pullCollection, recRoot, colRoot, type PullProgress } from "./pull.ts";
import { handleWriter } from "./fsdir.ts";

const PKG = "com.Fluid.XRBase";

const $ = (id: string) => document.getElementById(id)!;
const logEl = $("log") as HTMLPreElement;
const chip = $("devChip");
const rowsEl = $("rows");
const btnConnect = $("btnConnect") as HTMLButtonElement;
const btnDisconnect = $("btnDisconnect") as HTMLButtonElement;
const btnFolder = $("btnFolder") as HTMLButtonElement;
const btnRefresh = $("btnRefresh") as HTMLButtonElement;
const btnPullAll = $("btnPullAll") as HTMLButtonElement;
const optDelete = $("optDelete") as HTMLInputElement;
const outDirEl = $("outDir");
const progressEl = $("progress");
const progFile = $("progFile");
const progBar = $("progBar") as HTMLElement;

function log(msg: string) {
  logEl.textContent += msg + "\n";
  logEl.scrollTop = logEl.scrollHeight;
  console.log("[ingest]", msg);
}
function setChip(msg: string, cls: "" | "ok" | "err" = "") {
  chip.textContent = msg;
  chip.className = cls;
}
const fmtMB = (b: number) => (b / 1048576).toFixed(1) + " MB";

const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
const credentialStore = new AdbWebCredentialStore();

let adb: Adb | undefined;
let connectP: Promise<Adb> | null = null;   // serialize connects — two dispatchers on one USB endpoint corrupt the packet stream
let outRoot: FileSystemDirectoryHandle | undefined;
let remoteSessions: { id: string; files: number; bytes: number }[] = [];
let remoteCollections: { id: string; files: number; bytes: number }[] = [];

optDelete.checked = localStorage.getItem("ingest-delete-remote") === "1";
optDelete.addEventListener("change", () =>
  localStorage.setItem("ingest-delete-remote", optDelete.checked ? "1" : "0"));

async function connect(device: any): Promise<Adb> {
  if (connectP) return connectP;
  connectP = (async () => {
    if (adb) await disconnect();   // never run two transports over one endpoint
    setChip(`connecting ${device.serial}…`);
    const connection = await device.connect();
    const transport = await AdbDaemonTransport.authenticate({
      serial: device.serial, connection, credentialStore,
    });
    adb = new Adb(transport);
    // Surface a mid-session transport death (unplug, another adb owner, protocol
    // desync) instead of leaving the UI "connected" with every op rejecting.
    (transport as any).disconnected?.then?.(
      () => { if (adb) markDisconnected("transport closed"); },
      (err: any) => { if (adb) markDisconnected(err?.message ?? String(err)); },
    );
    btnDisconnect.disabled = false;
    btnRefresh.disabled = false;
    setChip(device.serial, "ok");
    log(`connected: ${device.serial}`);
    return adb;
  })();
  try { return await connectP; } finally { connectP = null; }
}

// A dead transport rejects every subsequent write with closed/closing-stream
// errors; a protocol desync surfaces as "Unknown command". Both mean the
// connection is gone — reset so the next Refresh/Connect re-establishes it.
function isTransportDead(err: any): boolean {
  return /CLOSED writable|closing writable|Unknown command|disconnected|NetworkError|transfer(In|Out)/i
    .test(String(err?.message ?? err));
}
function markDisconnected(reason: string) {
  adb = undefined;
  btnDisconnect.disabled = true;
  btnPullAll.disabled = true;
  btnRefresh.disabled = false;   // Refresh auto-reconnects via the persisted grant
  setChip("connection lost — Refresh to reconnect", "err");
  log(`connection lost: ${reason}`);
}

async function autoConnect(): Promise<Adb | null> {
  if (!manager) { setChip("WebUSB unavailable", "err"); return null; }
  if (adb) return adb;
  const devices = await manager.getDevices();
  if (!devices.length) { setChip("no granted device — Connect…"); return null; }
  try {
    return await connect(devices[0]);
  } catch (err: any) {
    setChip("error", "err");
    log(`connect failed: ${err?.message ?? err}` +
        (String(err).includes("claim") ? "  (another adb owner? quit MQDH / adb kill-server)" : ""));
    return null;
  }
}

async function disconnect() {
  try { await adb?.close(); } catch { /* already gone */ }
  adb = undefined;
  btnDisconnect.disabled = true;
  btnRefresh.disabled = true;
  btnPullAll.disabled = true;
  setChip("disconnected");
  log("disconnected — native adb can reclaim the device");
}

function onProgress(p: PullProgress) {
  progressEl.style.display = "";
  if (p.phase === "pull" && p.file) {
    progFile.textContent = `${p.file}  (${p.filesDone + 1}/${p.filesTotal}, ${fmtMB(p.bytesDone)} / ${fmtMB(p.bytesTotal)})`;
  } else {
    progFile.textContent = p.phase;
  }
  progBar.style.width = (p.bytesTotal ? Math.round((p.bytesDone / p.bytesTotal) * 100) : 0) + "%";
  if (p.phase === "done") setTimeout(() => { progressEl.style.display = "none"; }, 1200);
}

async function refresh() {
  if (!adb && !(await autoConnect())) return;
  setChip("listing…", "ok");
  try {
    remoteSessions = await listRemote(adb!, recRoot(PKG));
    remoteCollections = await listRemote(adb!, colRoot(PKG));
  } catch (err: any) {
    log(`list failed: ${err?.message ?? err}`);
    if (isTransportDead(err)) markDisconnected(err?.message ?? String(err));
    else setChip("error", "err");
    return;
  }
  setChip(`${remoteSessions.length} sessions · ${remoteCollections.length} collections`, "ok");
  await renderRows();
}

async function renderRows() {
  const entries = [
    ...remoteCollections.map((c) => ({ ...c, kind: "collection" as const })),
    ...remoteSessions.map((s) => ({ ...s, kind: "session" as const })),
  ];
  if (!entries.length) {
    rowsEl.innerHTML = `<tr><td colspan="4" style="color:var(--muted)">Nothing on the headset.</td></tr>`;
    btnPullAll.disabled = true;
    return;
  }
  const rows: string[] = [];
  let anyMissing = false;
  for (const e of entries) {
    const local = outRoot ? await handleWriter(outRoot).exists(e.id) : false;
    if (!local) anyMissing = true;
    rows.push(`<tr class="${local ? "local" : ""}">
      <td>${e.id}${e.kind === "collection" ? '<span class="tag">collection</span>' : ""}${local ? '<span class="tag">local</span>' : ""}</td>
      <td class="num">${e.files}</td>
      <td class="num">${fmtMB(e.bytes)}</td>
      <td>${local ? "" : `<button class="secondary" data-pull="${e.id}" data-kind="${e.kind}">Pull</button>`}</td>
    </tr>`);
  }
  rowsEl.innerHTML = rows.join("");
  btnPullAll.disabled = !outRoot || !anyMissing;
  for (const b of rowsEl.querySelectorAll<HTMLButtonElement>("[data-pull]")) {
    b.addEventListener("click", () => pullOne(b.dataset.pull!, b.dataset.kind as "session" | "collection"));
  }
}

async function pullOne(id: string, kind: "session" | "collection") {
  if (!adb) return log("not connected");
  if (!outRoot) return log("pick a local sessions folder first");
  const writer = handleWriter(outRoot);
  const del = optDelete.checked;
  log(`pulling ${kind} ${id}${del ? " (delete-after-verify)" : " (keep remote)"}…`);
  try {
    const fn = kind === "collection" ? pullCollection : pullSession;
    const r = await fn(adb, PKG, id, writer, { deleteRemote: del, onProgress });
    const mbps = r.seconds > 0 ? (r.bytes / 1048576 / r.seconds).toFixed(1) : "?";
    log(`  ${r.id}: ${r.files} files, ${fmtMB(r.bytes)} in ${r.seconds.toFixed(1)}s (${mbps} MiB/s) — ` +
        (r.verified ? "verified ✓" : `MISMATCH: ${r.mismatches.join(", ")}`) +
        (r.deletedRemote ? ", remote removed" : ""));
    await refresh();
  } catch (err: any) {
    log(`  ERROR pulling ${id}: ${err?.message ?? err}`);
    if (isTransportDead(err)) markDisconnected(err?.message ?? String(err));
  }
}

async function pullAllMissing() {
  if (!adb || !outRoot) return;
  const writer = handleWriter(outRoot);
  for (const c of remoteCollections)
    if (!(await writer.exists(c.id))) await pullOne(c.id, "collection");
  for (const s of remoteSessions)
    if (!(await writer.exists(s.id))) await pullOne(s.id, "session");
}

btnConnect.onclick = async () => {
  if (!manager) return setChip("WebUSB unavailable", "err");
  try {
    const device = await manager.requestDevice();
    if (!device) return;
    await connect(device);
    await refresh();
  } catch (err: any) {
    setChip("error", "err");
    log(`connect failed: ${err?.message ?? err}`);
  }
};
btnDisconnect.onclick = disconnect;
btnRefresh.onclick = refresh;
btnPullAll.onclick = pullAllMissing;
btnFolder.onclick = async () => {
  try {
    outRoot = await (window as any).showDirectoryPicker({ mode: "readwrite" });
    outDirEl.textContent = `Local sessions folder: ${outRoot!.name}`;
    log(`local folder: ${outRoot!.name}`);
    await renderRows();
  } catch { /* user cancelled */ }
};

// Boot: reuse an existing grant silently.
(async () => {
  const a = await autoConnect();
  if (a) await refresh();
})();

// ── E2E hooks (automation only) ─────────────────────────────────────────────
// Pulls the newest session into OPFS (no permission dialogs) and returns a
// JSON-able result. keepRemote defaults to true so automation is non-destructive.
(window as any).__e2e = {
  connect: autoConnect,
  list: async () => {
    if (!(await autoConnect())) throw new Error("no device");
    await refresh();
    return { sessions: remoteSessions, collections: remoteCollections };
  },
  pullNewestToOpfs: async (opts?: { deleteRemote?: boolean }) => {
    if (!(await autoConnect())) throw new Error("no device");
    await refresh();
    const newest = remoteSessions[remoteSessions.length - 1];
    if (!newest) throw new Error("no sessions on device");
    const opfs = await navigator.storage.getDirectory();
    const writer = handleWriter(opfs);
    return await pullSession(adb!, PKG, newest.id, writer, {
      deleteRemote: opts?.deleteRemote ?? false, onProgress,
    });
  },
  opfsClear: async () => {
    const opfs = await navigator.storage.getDirectory();
    for await (const [name] of (opfs as any).entries()) {
      await opfs.removeEntry(name, { recursive: true });
    }
    return "cleared";
  },
  disconnect,
};
